import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Code, Zap, TrendingUp, BarChart3, Key, ArrowRight, Globe, Play, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const ENDPOINTS = [
  {
    method: 'GET',
    path: '/api/v1/edge',
    title: 'Get Current Edge',
    desc: 'Returns the winning strategy for an industry right now.',
    params: [{ name: 'industry', type: 'string', desc: 'Filter by industry (e.g., saas, fintech, ai)', required: false }],
    icon: Zap,
    color: '#FF8A3D',
    tryUrl: '/api/v1/edge?industry=saas'
  },
  {
    method: 'GET',
    path: '/api/v1/leaderboard',
    title: 'Strategy Leaderboard',
    desc: 'Top performing strategies ranked by network performance.',
    params: [],
    icon: TrendingUp,
    color: '#22C55E',
    tryUrl: '/api/v1/leaderboard'
  },
  {
    method: 'GET',
    path: '/api/v1/signals',
    title: 'Live Market Signals',
    desc: 'Real-time market intelligence signals across industries.',
    params: [
      { name: 'industry', type: 'string', desc: 'Filter by industry', required: false },
      { name: 'limit', type: 'number', desc: 'Max signals (default 10)', required: false }
    ],
    icon: BarChart3,
    color: '#3B82F6',
    tryUrl: '/api/v1/signals?limit=3'
  },
  {
    method: 'GET',
    path: '/api/v1/trends',
    title: 'Network Trends',
    desc: "What's working and what's declining across the network.",
    params: [],
    icon: TrendingUp,
    color: '#8B5CF6',
    tryUrl: '/api/v1/trends'
  },
  {
    method: 'GET',
    path: '/api/v1/keys/generate',
    title: 'Generate API Key',
    desc: 'Generate a free API key for your application.',
    params: [{ name: 'name', type: 'string', desc: 'App name for tracking', required: false }],
    icon: Key,
    color: '#F59E0B',
    tryUrl: '/api/v1/keys/generate?name=my-app'
  },
];

export default function DevelopersPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');
  const [tryResult, setTryResult] = useState({});
  const [trying, setTrying] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);

  useEffect(() => {
    document.body.style.backgroundColor = '#07070A';
    document.body.style.color = '#fff';
    return () => { document.body.style.backgroundColor = ''; document.body.style.color = ''; };
  }, []);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(''), 1500);
  };

  const tryEndpoint = async (ep) => {
    setTrying(ep.path);
    try {
      const res = await axios.get(`${API}${ep.tryUrl.replace('/api', '')}`, { timeout: 30000 });
      setTryResult(p => ({ ...p, [ep.path]: JSON.stringify(res.data, null, 2) }));
      if (ep.path.includes('keys/generate') && res.data?.api_key) setGeneratedKey(res.data.api_key);
    } catch (e) {
      setTryResult(p => ({ ...p, [ep.path]: `Error: ${e.message}` }));
    } finally { setTrying(''); }
  };

  return (
    <div className="min-h-screen font-landing relative" style={{ background: '#07070A' }} data-testid="developers-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="bg-grid-animated absolute inset-0" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(124,92,255,0.06) 0%, transparent 70%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-8 pb-2">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors" data-testid="dev-back"><ArrowLeft className="w-4 h-4" /></button>
          <div className="flex-1" />
          <button onClick={() => navigate('/gallery')} className="text-[10px] font-mono text-zinc-500 hover:text-white transition-colors">Gallery</button>
        </div>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] px-3 py-1.5 rounded-full mb-4" style={{ color: '#7C5CFF', background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)' }}>
            <Code className="w-3 h-3" />Strategy API
          </div>
          <h1 className="text-3xl font-bold text-gradient-purple mb-2">Build on NextMove</h1>
          <p className="text-sm text-zinc-500 max-w-lg mx-auto">Integrate live marketing intelligence into your product. Real-time strategy data from our global network.</p>
        </div>

        {/* Quick Start */}
        <div className="rounded-2xl p-5 mb-8" style={{ background: '#0F0F14', border: '1px solid rgba(124,92,255,0.1)' }}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-[#7C5CFF] mb-3">Quick Start</p>
          <div className="rounded-xl p-4 font-mono text-[12px] relative" style={{ background: '#07070A', border: '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={() => copyText(`curl "${BASE}/api/v1/edge?industry=saas"`, 'quickstart')} className="absolute top-3 right-3 text-zinc-600 hover:text-white transition-colors">
              {copied === 'quickstart' ? <Check className="w-3.5 h-3.5 text-[#22C55E]" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            <p className="text-zinc-500"># Get the winning strategy for SaaS right now</p>
            <p><span className="text-[#22C55E]">curl</span> <span className="text-[#FF8A3D]">"{BASE}/api/v1/edge?industry=saas"</span></p>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="rounded-lg px-3 py-2" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
              <p className="text-[9px] font-mono text-zinc-600 uppercase">Pricing</p>
              <p className="text-sm font-bold text-[#22C55E]">Free</p>
              <p className="text-[9px] text-zinc-600">100 req/hour</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
              <p className="text-[9px] font-mono text-zinc-600 uppercase">Latency</p>
              <p className="text-sm font-bold text-white">&lt;100ms</p>
              <p className="text-[9px] text-zinc-600">Cached data</p>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: '#15151D', border: '1px solid rgba(255,255,255,0.04)' }}>
              <p className="text-[9px] font-mono text-zinc-600 uppercase">Format</p>
              <p className="text-sm font-bold text-white">JSON</p>
              <p className="text-[9px] text-zinc-600">REST API</p>
            </div>
          </div>
        </div>
      </div>

      {/* Endpoints */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-8 space-y-4">
        {ENDPOINTS.map((ep) => {
          const Icon = ep.icon;
          const result = tryResult[ep.path];
          return (
            <div key={ep.path} className="rounded-2xl overflow-hidden" data-testid={`endpoint-${ep.path.split('/').pop()}`}
              style={{ background: '#0F0F14', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-3 px-5 py-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${ep.color}10`, border: `1px solid ${ep.color}20` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: ep.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded" style={{ background: '#22C55E15', color: '#22C55E' }}>{ep.method}</span>
                    <code className="text-[12px] font-mono text-zinc-300">{ep.path}</code>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{ep.desc}</p>
                </div>
                <button onClick={() => tryEndpoint(ep)} disabled={trying === ep.path} data-testid={`try-${ep.path.split('/').pop()}`}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-medium text-[#7C5CFF] hover:text-white transition-all"
                  style={{ background: 'rgba(124,92,255,0.06)', border: '1px solid rgba(124,92,255,0.12)' }}>
                  {trying === ep.path ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}Try it
                </button>
              </div>

              {/* Params */}
              {ep.params.length > 0 && (
                <div className="px-5 pb-2">
                  <div className="flex gap-2">
                    {ep.params.map(p => (
                      <span key={p.name} className="text-[9px] font-mono px-2 py-0.5 rounded" style={{ background: '#15151D', color: '#7C5CFF' }}>
                        {p.name}<span className="text-zinc-600">: {p.type}</span>{!p.required && <span className="text-zinc-700"> (optional)</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Code snippets */}
              <div className="px-5 pb-3">
                <div className="flex gap-2 mb-1.5">
                  {['curl', 'python', 'javascript'].map(lang => {
                    const snippets = {
                      curl: `curl "${BASE}${ep.tryUrl}"`,
                      python: `import requests\nres = requests.get("${BASE}${ep.tryUrl}")\ndata = res.json()`,
                      javascript: `const res = await fetch("${BASE}${ep.tryUrl}");\nconst data = await res.json();`
                    };
                    return (
                      <button key={lang} onClick={() => copyText(snippets[lang], `${ep.path}-${lang}`)}
                        className="text-[9px] font-mono px-2 py-0.5 rounded flex items-center gap-1 transition-all"
                        style={{ color: copied === `${ep.path}-${lang}` ? '#22C55E' : '#52525B', background: '#15151D' }}>
                        {copied === `${ep.path}-${lang}` ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}{lang}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Response */}
              {result && (
                <div className="px-5 pb-4">
                  <div className="rounded-xl p-3 font-mono text-[10px] max-h-48 overflow-auto" style={{ background: '#07070A', border: '1px solid rgba(255,255,255,0.04)', scrollbarWidth: 'thin', scrollbarColor: '#1F1F28 transparent' }}>
                    <pre className="text-zinc-400 whitespace-pre-wrap">{result.length > 1000 ? result.slice(0, 1000) + '\n...' : result}</pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Generated Key */}
      {generatedKey && (
        <div className="relative z-10 max-w-4xl mx-auto px-4 pb-8">
          <div className="rounded-2xl px-5 py-4" style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)' }} data-testid="generated-key-display">
            <p className="text-[10px] font-mono uppercase tracking-wider text-[#22C55E] mb-1">Your API Key</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-white flex-1">{generatedKey}</code>
              <button onClick={() => copyText(generatedKey, 'apikey')} className="text-zinc-500 hover:text-white transition-colors">
                {copied === 'apikey' ? <Check className="w-4 h-4 text-[#22C55E]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[9px] text-zinc-600 mt-1 font-mono">Free tier: 100 requests/hour. Pass via X-API-Key header.</p>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 pb-16 text-center">
        <h2 className="text-xl font-bold text-white mb-2">Turn your product into a growth machine.</h2>
        <p className="text-sm text-zinc-500 mb-5">Integrate NextMove intelligence and give your users a competitive edge.</p>
        <button onClick={() => navigate('/')} data-testid="dev-cta"
          className="h-12 px-10 rounded-2xl text-white text-sm font-semibold btn-glow inline-flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #7C5CFF, #FF8A3D)', boxShadow: '0 6px 25px rgba(124,92,255,0.25)' }}>
          Start Building <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center py-8 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <p className="text-[11px] text-zinc-700 font-mono">NextMove Strategy API v1 — Global Growth Intelligence</p>
      </div>
    </div>
  );
}
